import { Box, LinearProgress, Stack } from "@mui/material";
import { take } from "lodash";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../misc/useLimit";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import type { AppContext } from "../orders/AppContext";
import OrdersActions from "../orders/OrdersActions";
import OrdersSearch from "../orders/topBar/CustomerOrdersSearch";
import type { OrdersSearchCriteria } from "../orders/topBar/useOrderSearch";
import { useFilteredOrders } from "../orders/useFilteredOrders";
import { useOrdersSearchFromQueryParams } from "../orders/useOrdersSearchFromQueryParams";
import { OrderCard } from "./orderCard/OrderCard";

interface Props {
  readonly search: OrdersSearchCriteria;
  readonly onSearchChange: (value: Partial<OrdersSearchCriteria>) => void;
  readonly onSearchClear: () => void;
}

const OrdersPage: FC<Props> = ({ search, onSearchChange, onSearchClear }) => {
  const navigate = useNavigate();
  const { openChildInNewTab } = useOpenInNewTab();
  const { limit, loadMore, resetLimit } = useLimit();
  const [appContext, setAppContext] = useState<AppContext>({
    showHistorical: false,
    selectedOrders: [],
  });
  const { context } = useAuthContext();
  const { orders, isLoading } = useFilteredOrders(search, appContext.showHistorical);

  const handleFiltersChange = (value: Partial<OrdersSearchCriteria>) => {
    onSearchChange(value);
    resetLimit();
  };

  useOrdersSearchFromQueryParams({
    fallbackAgentId: search.agentId,
    onApply: handleFiltersChange,
  });

  const role = context?.role;
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: "1.5em",
        height: "100%",
      }}
    >
      <OrdersSearch
        search={search}
        context={context ?? undefined}
        showHistorical={appContext.showHistorical}
        onChange={(v) => handleFiltersChange(v)}
        onShowHistoricalChange={(showHistorical) => setAppContext((ctx) => ({ ...ctx, showHistorical }))}
        onClear={onSearchClear}
        onViewModeChange={(viewMode) => setViewMode(viewMode)}
        viewMode={viewMode}
      />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < orders.length ? limit : orders.length}
        hasMore={orders.length > limit}
        fetchData={loadMore}
        scrollableTarget={null}
      >
        <Box
          sx={
            viewMode === "grid"
              ? { columnCount: { xs: 1, lg: 2 }, columnGap: "24px" }
              : { display: "flex", flexDirection: "column", gap: 3 }
          }
        >
          {take(orders, limit).map((o) => (
            <OrderCard
              key={o.id}
              role={role}
              value={o}
              showHistorical={appContext.showHistorical}
              onClick={() => navigate(o.id)}
              onContextMenu={() => openChildInNewTab(o.id)}
              viewMode={viewMode}
            />
          ))}
        </Box>
      </InfiniteScroll>
      {context && <OrdersActions userContext={context} onAction={() => navigate("new")} />}
    </Stack>
  );
};

export default OrdersPage;
